import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as authApi from '@/features/auth/api';
import { ApiError } from '@/lib/api/client';

const GENERIC_INVALID_MESSAGE = 'Tautan ini tidak valid atau sudah kedaluwarsa.';

/**
 * `POST /auth/reset-password` (Phase G-7, Job 7). Captures `?token=` on
 * mount and immediately scrubs it from the URL/history (same discipline as
 * `features/guardian/accept-invitation-page.tsx`, Phase G-0 Job 11) - held
 * only in component state from then on, never re-added to the URL, since
 * this is a single-step form with no redirect to survive.
 */
export function ResetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [missingToken, setMissingToken] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken !== null && urlToken.length > 0) {
      setToken(urlToken);
      void navigate('/reset-password', { replace: true });
      return;
    }
    if (token === null) {
      setMissingToken(true);
    }
    // Only runs once per mount: the token, once captured, is scrubbed from
    // the URL above, so this effect must not re-fire on the resulting
    // navigation and treat the now-clean URL as "missing".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (submitting || token === null) {
      return;
    }
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Konfirmasi kata sandi tidak cocok.');
      return;
    }

    setSubmitting(true);
    authApi
      .resetPassword({ token, newPassword })
      .then(() => {
        setSubmitted(true);
      })
      .catch((caught: unknown) => {
        setError(
          caught instanceof ApiError && caught.code === 'INVALID_VERIFICATION_TOKEN'
            ? GENERIC_INVALID_MESSAGE
            : caught instanceof ApiError
              ? caught.message
              : 'Terjadi kesalahan. Silakan coba lagi.',
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  if (missingToken) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <h1 className="sr-only">Tautan Tidak Ditemukan</h1>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Tautan Tidak Ditemukan</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{GENERIC_INVALID_MESSAGE}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <h1 className="sr-only">Kata Sandi Diperbarui</h1>
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>Kata Sandi Diperbarui</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert>
              <AlertDescription>
                Kata sandi Anda telah diperbarui. Semua sesi aktif sebelumnya telah diakhiri untuk
                keamanan.
              </AlertDescription>
            </Alert>
            <Button asChild>
              <Link to="/login">Masuk dengan Kata Sandi Baru</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <h1 className="sr-only">Atur Ulang Kata Sandi</h1>
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Atur Ulang Kata Sandi</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="reset-password-new">Kata Sandi Baru</Label>
              <Input
                id="reset-password-new"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                }}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="reset-password-confirm">Konfirmasi Kata Sandi Baru</Label>
              <Input
                id="reset-password-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                }}
                required
              />
            </div>
            {error !== null && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Memproses...' : 'Atur Ulang Kata Sandi'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
